import type { RecordOf, RecordSource, RecordSourceKey, RecordSummary } from '@/helpers/records/recordSources'
import { RECORD_SOURCES } from '@/helpers/records/recordSources'
import type { ModuleKey } from '@/utils/constants/navigationLabels'

// Un resumen por registro, compartido por el breadcrumb, los títulos de pestaña y los formularios de Resumen.
const summaries = new Map<string, RecordSummary>()
const pendingRequests = new Map<string, Promise<unknown>>()
const failedRecords = new Set<string>()
const listeners = new Set<() => void>()

const toKey = (moduleKey: ModuleKey, recordId: string | number) => `${moduleKey}:${recordId}`

const notify = () => listeners.forEach(listener => listener())

export const subscribeRecords = (listener: () => void) => {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

export const hasRecordSource = (moduleKey: ModuleKey): moduleKey is RecordSourceKey =>
  Object.hasOwn(RECORD_SOURCES, moduleKey)

export const getRecordSummary = (moduleKey: ModuleKey, recordId: string | number) =>
  summaries.get(toKey(moduleKey, recordId))

export const hasRecordFailed = (moduleKey: ModuleKey, recordId: string | number) =>
  failedRecords.has(toKey(moduleKey, recordId))

// Los formularios lo usan en lugar del servicio: traen datos frescos y comparten la petición si ya hay una en curso.
export const fetchRecord = <K extends RecordSourceKey>(
  moduleKey: K,
  recordId: string | number
): Promise<RecordOf<K>> => {
  const key = toKey(moduleKey, recordId)
  const pending = pendingRequests.get(key)

  if (pending) return pending as Promise<RecordOf<K>>

  // Cada módulo está tipado en su fuente; aquí se trata como genérico porque el resumen solo consume lo que ese mismo módulo cargó.
  const source = RECORD_SOURCES[moduleKey] as unknown as RecordSource<unknown>

  const request = source.load(String(recordId)).then(record => {
    failedRecords.delete(key)
    summaries.set(key, source.summarize(record))
    notify()

    return record
  })

  pendingRequests.set(key, request)

  const release = () => pendingRequests.delete(key)

  request.then(release, () => {
    release()
    failedRecords.add(key)
    notify()
  })

  return request as Promise<RecordOf<K>>
}

// Se llama al guardar un registro: descarta el resumen y quien lo muestre lo vuelve a pedir.
export const invalidateRecord = (moduleKey: ModuleKey, recordId: string | number) => {
  const key = toKey(moduleKey, recordId)

  failedRecords.delete(key)
  summaries.delete(key)
  notify()
}
